import { useCallback, useEffect, useRef } from 'react';
import usePoColumns from 'src/assets/static/new_receipt/usePoColumns';
import SearchInput from 'src/components/SearchInput';
import PermissionGuard from 'src/components/guards/PermissionGuard';
import { CanAccessReceipts } from 'src/components/guards/PermissionsList';
import useAttachDocument from 'src/hooks/useAttachDocument';
import useBoolean from 'src/hooks/useBoolean';
import useEnterReceipts from 'src/hooks/useEnterReceipts';
import usePermission from 'src/hooks/usePermissions';
import { poModal, receiptModal } from 'src/sections/enter_receipt/GuideMeSteps';
import PoGrid from 'src/sections/enter_receipt/PoGrid';
import ReceiptGrid from 'src/sections/enter_receipt/ReceiptGrid';
import ClosePoWizard from 'src/sections/enter_receipt/enter_receipt_wizard/ClosePoWizard';
import CreateReceiptWithoutPoWizard from 'src/sections/enter_receipt/enter_receipt_wizard/CreateReceiptWithoutPoWizard';
import { IPO } from 'src/types/PO';
import AttachDocumentWizard from '../attach_document/AttachDocumentWizard';
import RequirePermissionWizard from '../enter_invoice/enter_invoice_wizard/RequirePermissionWizard';

const EnterReceiptMain = () => {
  const {
    currentStep,
    selectedCompany: { companyId },
    searchPo,
    setSearchPo,
    supplierSearch,
    setSupplierSearch,
    lineItemSearch,
    setLineItemSearch,
    selectedReceiptPoRow,
    setSelectedReceiptPoRow,
    selectedReceiptRow,
    setSelectedReceiptRow,
    setDisableStep,
    setGuideMeModalData,
    openReceiptModal,
    openPoCloseModal,
    openGuideMe,
    openExistingAttachmentsModal,
    toggleReceiptModal,
    togglePoCloseModal,
    toggleExistingAttachmentsModal,
    nextStep,
    poTotal,
    setPoTotal,
    changeType,
    setChangeType,
    showPreview,
  } = useEnterReceipts();

  const { getAttachDocuments } = useAttachDocument();
  const poColumns = usePoColumns();
  const { value: isViewReceiptsPermissionWizard, toggle: toggleViewReceiptsPermissionWizard } =
    useBoolean(true);

  const hasReceiptAccess = usePermission({
    permissions: CanAccessReceipts,
    clientId: String(selectedReceiptPoRow?.buyerId),
    or: true,
  });

  useEffect(() => {
    if (currentStep === 1) {
      setGuideMeModalData([poModal]);
      setSearchPo((prev) => ({ ...prev, disable: false }));
      if (companyId) {
        openGuideMe();
      }
    } else if (currentStep === 2) {
      setSearchPo((prev) => ({ ...prev, disable: true }));
      if (hasReceiptAccess) {
        setGuideMeModalData([receiptModal]);
        if (companyId) {
          openGuideMe();
        }
      }
    }
  }, [
    currentStep,
    openGuideMe,
    companyId,
    setDisableStep,
    setGuideMeModalData,
    setSearchPo,
    hasReceiptAccess,
  ]);

  useEffect(() => {
    if (companyId) getAttachDocuments();
  }, [companyId, getAttachDocuments]);

  const handlePoSearchKeyDown = () => {
    nextStep();
  };

  const searchPoRef = useRef<HTMLInputElement | null>(null);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const currentInput = event.target.value;
    if (currentInput.length > searchPo.value.length) {
      setChangeType('Added');
    } else if (currentInput.length < searchPo.value.length) {
      setChangeType('Removed');
    } else {
      setChangeType('');
    }
    setSearchPo({ disable: false, value: currentInput });
  };

  useEffect(() => {
    if (changeType === 'Removed' && poTotal > 1 && searchPoRef.current) {
      searchPoRef.current?.blur();
    }
  }, [changeType, poTotal]);

  const handleSelectedSupplier = useCallback(
    (data: IPO) => {
      setSelectedReceiptPoRow(data);
    },
    [setSelectedReceiptPoRow]
  );

  return (
    <main className="h-full">
      <div>
        <div className="mt-2 grid grid-cols-3 gap-3">
          <SearchInput
            width="100%"
            title="PO #"
            value={searchPo.value}
            onChange={handleInputChange}
            placeholder="Enter PO # (Optional)"
            disabled={searchPo.disable}
            onKeyDown={handlePoSearchKeyDown}
            inputRef={searchPoRef}
          />
          {currentStep === 1 && (
            <>
              <SearchInput
                width="100%"
                title="Filter PO's by Supplier Name"
                placeholder="Enter supplier name (Optional)"
                value={supplierSearch}
                onChange={(e) => {
                  setSupplierSearch(e.target.value);
                }}
              />
              <SearchInput
                width="100%"
                title="Filter PO's by Item"
                value={lineItemSearch}
                placeholder="Enter item # or description (Optional)"
                onChange={(e) => {
                  setLineItemSearch(e.target.value);
                }}
              />
            </>
          )}
        </div>
        {currentStep === 1 && (
          <>
            <PoGrid
              poNumber={searchPo.value}
              setSelectedReceiptPoRow={handleSelectedSupplier}
              companyId={companyId}
              supplierSearch={supplierSearch}
              setSupplierSearch={setSupplierSearch}
              lineItemSearch={lineItemSearch}
              setLineItemSearch={setLineItemSearch}
              columns={poColumns}
              setDisableStep={setDisableStep}
              setPoSearch={setSearchPo}
              setTotal={setPoTotal}
              isInvoice={false}
              isDetailsPage={false}
              key={`${showPreview}`}
            />
            <CreateReceiptWithoutPoWizard
              closeModal={toggleReceiptModal}
              openModal={openReceiptModal}
            />
          </>
        )}
        {currentStep === 2 && (
          <>
            <PermissionGuard
              permissions={CanAccessReceipts}
              clientId={String(selectedReceiptPoRow?.buyerId)}
              or={true}
              fallback={
                <RequirePermissionWizard
                  title={'Cannot View Receipts'}
                  content={'You are not authorized to view receipts for selected PO'}
                  openModal={isViewReceiptsPermissionWizard}
                  closeModal={toggleViewReceiptsPermissionWizard}
                />
              }
            >
              <ReceiptGrid
                companyId={companyId}
                isDetailsPage={false}
                selectedReceiptPoRow={selectedReceiptPoRow}
                setSelectedReceiptRow={setSelectedReceiptRow}
                setDisableStep={setDisableStep}
                key={`${showPreview}`}
              />
            </PermissionGuard>
            <ClosePoWizard
              poId={selectedReceiptPoRow?.poId}
              receiptId={selectedReceiptRow?.receivingReceiptId}
              closeModal={togglePoCloseModal}
              openModal={openPoCloseModal}
            />
            <PermissionGuard
              permissions={CanAccessReceipts}
              clientId={String(selectedReceiptPoRow?.buyerId)}
              or={true}
              fallback={
                <RequirePermissionWizard
                  title={'Cannot Create Receipt'}
                  content={'You are not authorized to generate a Receipt for selected PO'}
                  openModal={openExistingAttachmentsModal}
                  closeModal={toggleExistingAttachmentsModal}
                />
              }
            >
              <AttachDocumentWizard
                openModal={openExistingAttachmentsModal}
                closeModal={toggleExistingAttachmentsModal}
                title={
                  selectedReceiptRow?.receivingReceiptId
                    ? 'Attach to Existing Receipt'
                    : 'Attach to New Receipt'
                }
                noOfAttachments={selectedReceiptRow?.numOfAttachments}
              />
            </PermissionGuard>
          </>
        )}
      </div>
    </main>
  );
};

export default EnterReceiptMain;
